import {isAfter, isBefore, min, parseISO} from 'date-fns';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {INTEREST_SIGNAL_CODES} from '../constants/INTEREST_SIGNAL_CODES';
import type {InterestSignalEvidenceOmission} from '../constants/INTEREST_SIGNAL_EVIDENCE_OMISSIONS';
import type {InterestFacts} from '../types/InterestFacts';
import type {InterestPlannedActivity} from '../types/InterestPlannedActivity';
import type {InterestSignal} from '../types/InterestSignal';
import type {InterestSignalCode} from '../types/InterestSignalCode';
import type {InterestSignalEvidence} from '../types/InterestSignalEvidence';
import type {InterestSignalPolicy} from '../types/InterestSignalPolicy';
import type {InterestSignalSeverity} from '../types/InterestSignalSeverity';
import type {InterestTimelineEventKind} from '../types/InterestTimelineEventKind';
import {differenceInWholeDays} from './differenceInWholeDays';
import {differenceInWholeHours} from './differenceInWholeHours';

export type DetectInterestSignalsInput = {
  facts: InterestFacts;
  policy: InterestSignalPolicy;
  evaluatedAt: string;
};

const NO_FIRST_RESPONSE_DANGER_THRESHOLD_HOURS = 24;

const INTEREST_GOING_COLD_SUPPRESSING_SIGNAL_CODES: readonly InterestSignalCode[] = [
  'customerAwaitingReply',
  'noFirstResponse',
];

const CUSTOMER_REPLY_ELIGIBLE_EVENT_KINDS: readonly InterestTimelineEventKind[] = [
  'message',
  'call',
  'email',
  'sms',
];

type PlannedActivityWithDueAt = InterestPlannedActivity & {dueAt: string};

const createInterestSignal = (
  code: InterestSignalCode,
  severity: InterestSignalSeverity,
  evidence: InterestSignalEvidence,
  evaluatedAt: string,
  evidenceOmissions: InterestSignalEvidenceOmission[] = []
): InterestSignal => ({
  code,
  severity,
  evidence,
  evaluatedAt,
  evidenceIncomplete: evidenceOmissions.length > 0,
  evidenceOmissions,
});

const getTimelineEvidenceOmissions = (
  input: DetectInterestSignalsInput
): InterestSignalEvidenceOmission[] =>
  input.facts.completeness.timelineComplete ? [] : ['timelineTruncated'];

const getPlannedActivityEvidenceOmissions = (
  input: DetectInterestSignalsInput
): InterestSignalEvidenceOmission[] => [
  ...((input.facts.completeness.omittedPlannedActivityCount ?? 0) > 0
    ? ['plannedActivitiesTruncated' as const]
    : []),
  ...(input.facts.completeness.plannedActivitiesComplete ? [] : ['otherUsersTasks' as const]),
];

const isAtOrAfter = (candidateAt: string, boundaryAt: string) =>
  !isBefore(parseISO(candidateAt), parseISO(boundaryAt));

const getAwaitingReplySeverity = (
  waitingSinceAt: string,
  input: DetectInterestSignalsInput
): InterestSignalSeverity =>
  match(differenceInWholeDays(input.evaluatedAt, waitingSinceAt) > input.policy.coldThresholdDays)
    .returnType<InterestSignalSeverity>()
    .with(true, () => 'danger')
    .otherwise(() => 'warning');

const getFirstResponseSeverity = (waitingSinceHours: number): InterestSignalSeverity =>
  match(waitingSinceHours > NO_FIRST_RESPONSE_DANGER_THRESHOLD_HOURS)
    .returnType<InterestSignalSeverity>()
    .with(true, () => 'danger')
    .otherwise(() => 'warning');

const detectCustomerAwaitingReply = (input: DetectInterestSignalsInput): InterestSignal | null => {
  const newestInboundEvent = input.facts.timeline.find(
    (event) =>
      event.direction === 'inbound' && CUSTOMER_REPLY_ELIGIBLE_EVENT_KINDS.includes(event.kind)
  );

  return match(newestInboundEvent)
    .returnType<InterestSignal | null>()
    .with(Pattern.nullish, () => null)
    .otherwise((inboundEvent) => {
      const hasReply = input.facts.timeline.some(
        (event) =>
          event.direction === 'outbound' && isAtOrAfter(event.occurredAt, inboundEvent.occurredAt)
      );
      const waitingSinceHours = differenceInWholeHours(input.evaluatedAt, inboundEvent.occurredAt);

      return match({
        hasReply,
        reachedMinimum: waitingSinceHours >= input.policy.awaitingReplyMinimumHours,
      })
        .returnType<InterestSignal | null>()
        .with({hasReply: false, reachedMinimum: true}, () =>
          createInterestSignal(
            'customerAwaitingReply',
            getAwaitingReplySeverity(inboundEvent.occurredAt, input),
            {
              waitingSinceAt: inboundEvent.occurredAt,
              waitingSinceHours,
              triggeringEventId: inboundEvent.id,
            },
            input.evaluatedAt,
            getTimelineEvidenceOmissions(input)
          )
        )
        .otherwise(() => null);
    });
};

const detectNoFirstResponse = (input: DetectInterestSignalsInput): InterestSignal | null => {
  const waitingSinceHours = differenceInWholeHours(input.evaluatedAt, input.facts.createdAt);
  const hasEligibleState = input.facts.state === 'new' || input.facts.state === 'open';
  const hasOutboundResponse = input.facts.timeline.some(
    (event) =>
      event.direction === 'outbound' && isAtOrAfter(event.occurredAt, input.facts.createdAt)
  );
  const firstResponseAtTime = isNotNil(input.facts.firstResponseAt)
    ? parseISO(input.facts.firstResponseAt).getTime()
    : null;
  // DMS can stamp firstResponseAt with the inbound message time. A complete first-party timeline
  // must prove the response with outbound evidence instead of trusting that echoed value.
  const firstResponseEchoesInboundEvent =
    isNotNil(firstResponseAtTime) &&
    input.facts.completeness.timelineComplete &&
    input.facts.completeness.firstResponseAtIsProxy !== true &&
    input.facts.timeline.some(
      (event) =>
        event.direction === 'inbound' &&
        parseISO(event.occurredAt).getTime() === firstResponseAtTime
    );
  const hasFirstResponse =
    hasOutboundResponse || (isNotNil(firstResponseAtTime) && !firstResponseEchoesInboundEvent);

  return match({
    kind: input.facts.kind,
    hasFirstResponse,
    hasEligibleState,
    reachedMinimum: waitingSinceHours >= input.policy.noFirstResponseMinimumHours,
  })
    .returnType<InterestSignal | null>()
    .with(
      {
        kind: 'sales',
        hasFirstResponse: false,
        hasEligibleState: true,
        reachedMinimum: true,
      },
      () =>
        createInterestSignal(
          'noFirstResponse',
          getFirstResponseSeverity(waitingSinceHours),
          {waitingSinceAt: input.facts.createdAt, waitingSinceHours},
          input.evaluatedAt,
          getTimelineEvidenceOmissions(input)
        )
    )
    .otherwise(() => null);
};

const detectGoingCold = (
  input: DetectInterestSignalsInput,
  emittedSignalCodes: ReadonlySet<InterestSignalCode>
): InterestSignal | null => {
  const lastActivityAt =
    input.facts.timeline[0]?.occurredAt ?? input.facts.updatedAt ?? input.facts.createdAt;
  const inactiveForDays = differenceInWholeDays(input.evaluatedAt, lastActivityAt);
  const isSuppressed = INTEREST_GOING_COLD_SUPPRESSING_SIGNAL_CODES.some((code) =>
    emittedSignalCodes.has(code)
  );

  return match({
    isSuppressed,
    exceededThreshold: inactiveForDays > input.policy.coldThresholdDays,
  })
    .returnType<InterestSignal | null>()
    .with({isSuppressed: false, exceededThreshold: true}, () =>
      createInterestSignal(
        'goingCold',
        'warning',
        {lastActivityAt, inactiveForDays},
        input.evaluatedAt,
        getTimelineEvidenceOmissions(input)
      )
    )
    .otherwise(() => null);
};

const detectMissingNextStep = (
  input: DetectInterestSignalsInput,
  emittedSignalCodes: ReadonlySet<InterestSignalCode>
): InterestSignal | null => {
  const hasCurrentOrFutureActivity = input.facts.plannedActivities.some(
    (activity) =>
      !activity.completed &&
      (isNil(activity.dueAt) || isAtOrAfter(activity.dueAt, input.evaluatedAt))
  );

  return match({hasCurrentOrFutureActivity, hasOverdueTask: emittedSignalCodes.has('overdueTask')})
    .returnType<InterestSignal | null>()
    .with({hasCurrentOrFutureActivity: false, hasOverdueTask: false}, () =>
      createInterestSignal(
        'missingNextStep',
        'info',
        {},
        input.evaluatedAt,
        getPlannedActivityEvidenceOmissions(input)
      )
    )
    .otherwise(() => null);
};

const isOverdueTask = (
  activity: InterestPlannedActivity,
  evaluatedAt: string
): activity is PlannedActivityWithDueAt =>
  activity.kind === 'task' &&
  !activity.completed &&
  isNotNil(activity.dueAt) &&
  isBefore(parseISO(activity.dueAt), parseISO(evaluatedAt));

const detectOverdueTask = (input: DetectInterestSignalsInput): InterestSignal | null => {
  const overdueTasks = input.facts.plannedActivities.filter((activity) =>
    isOverdueTask(activity, input.evaluatedAt)
  );

  return match(overdueTasks.length)
    .returnType<InterestSignal | null>()
    .when(
      (overdueTaskCount) => overdueTaskCount > 0,
      (overdueTaskCount) => {
        const oldestDueAt = min(overdueTasks.map((activity) => parseISO(activity.dueAt)));

        return createInterestSignal(
          'overdueTask',
          'warning',
          {
            overdueTaskCount,
            overdueBySinceDays: differenceInWholeDays(input.evaluatedAt, oldestDueAt.toISOString()),
            oldestOverdueAt: oldestDueAt.toISOString(),
          },
          input.evaluatedAt,
          getPlannedActivityEvidenceOmissions(input)
        );
      }
    )
    .otherwise(() => null);
};

const detectEngagement = (input: DetectInterestSignalsInput): InterestSignal | null => {
  const offerOpenedEvent = input.facts.timeline.find((event) => event.kind === 'offerOpened');
  const futureTestDrive = input.facts.plannedActivities.find(
    (activity) =>
      activity.kind === 'testDrive' &&
      !activity.completed &&
      isNotNil(activity.dueAt) &&
      isAfter(parseISO(activity.dueAt), parseISO(input.evaluatedAt))
  );
  const leadQualityEvidence = match(input.facts.leadQuality)
    .with(Pattern.nonNullable, (leadQuality) => ({leadQuality}))
    .otherwise(() => ({}));
  const evidence = match({offerOpenedEvent, futureTestDrive})
    .returnType<InterestSignalEvidence | null>()
    .with({offerOpenedEvent: Pattern.nonNullable}, ({offerOpenedEvent: event}) => ({
      engagementKind: 'offerOpened',
      triggeringEventId: event.id,
      ...leadQualityEvidence,
    }))
    .with({futureTestDrive: Pattern.nonNullable}, ({futureTestDrive: activity}) => ({
      engagementKind: 'testDrivePlanned',
      triggeringEventId: activity.id,
      ...leadQualityEvidence,
    }))
    .otherwise(() => null);

  return match({leadQuality: input.facts.leadQuality, evidence})
    .returnType<InterestSignal | null>()
    .with({leadQuality: 'hot'}, () => null)
    .with({evidence: Pattern.nonNullable}, ({evidence: engagementEvidence}) =>
      createInterestSignal('engagementDetected', 'info', engagementEvidence, input.evaluatedAt)
    )
    .otherwise(() => null);
};

const detectSignal = (
  code: InterestSignalCode,
  input: DetectInterestSignalsInput,
  emittedSignalCodes: ReadonlySet<InterestSignalCode>
): InterestSignal | null =>
  match(code)
    .returnType<InterestSignal | null>()
    .with('customerAwaitingReply', () => detectCustomerAwaitingReply(input))
    .with('noFirstResponse', () => detectNoFirstResponse(input))
    .with('overdueTask', () => detectOverdueTask(input))
    .with('engagementDetected', () => detectEngagement(input))
    .with('missingNextStep', () => detectMissingNextStep(input, emittedSignalCodes))
    .with('goingCold', () => detectGoingCold(input, emittedSignalCodes))
    .exhaustive();

export const detectInterestSignals = (input: DetectInterestSignalsInput): InterestSignal[] =>
  match(input.facts.state)
    .returnType<InterestSignal[]>()
    .with('closed', 'unsuccessful', () => [])
    .otherwise(() => {
      const enabledSignalCodes = new Set(input.policy.enabledSignalCodes);
      const signals: InterestSignal[] = [];
      const emittedSignalCodes = new Set<InterestSignalCode>();

      INTEREST_SIGNAL_CODES.filter((code) => enabledSignalCodes.has(code)).forEach((code) => {
        match(detectSignal(code, input, emittedSignalCodes))
          .with(Pattern.nonNullable, (signal) => {
            signals.push(signal);
            emittedSignalCodes.add(signal.code);
          })
          .otherwise(() => undefined);
      });

      return signals;
    });
